/**
 * Home: the agent's cockpit. Above the fold, the work: the week's scoreboard strip, who to call
 * next, today's agenda. Under it, how today is going and where I stand against the floor, then
 * my own book. Last, spec 6.1's Salesforce home charts, kept whole on Home because the floor runs
 * on seeing everyone's week. A manager also gets the floor's to-do first.
 *
 * Every card loads, fails and empties on its own, so one slow query never blanks the page.
 */
import { Link } from "react-router";

import { useCockpit, useEvents, useFloor, useHomeDashboard, useTasks } from "../api/hooks";
import { useMe } from "../auth/session";
import { CardBody } from "../components/kit";
import { ErrorState } from "../components/ui";
import { useNow } from "../lib/useNow";
import { Agenda } from "./home/Agenda";
import { Bests, CommissionMonth, Moments, Pipeline, WeekByDay } from "./home/Book";
import { Competition } from "./home/Competition";
import { Floor } from "./home/Floor";
import { Hero } from "./home/Hero";
import { ActivityRow, FloorWins, Leaderboard } from "./home/Standing";
import { UpNext } from "./home/UpNext";

function dayRange(now: number): { from: string; to: string } {
  const d = new Date(now);
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
  return { from: start.toISOString(), to: end.toISOString() };
}

export function Home() {
  const { me, can } = useMe();
  const now = useNow(1000);
  const minute = useNow(60_000);
  const cockpit = useCockpit();
  const dash = useHomeDashboard();
  const manager = can("records:reassign");
  const floor = useFloor(manager);
  const overdue = useTasks({ due: "overdue" });
  const today = useTasks({ due: "today" });
  const events = useEvents(dayRange(minute));
  const firstName = me.user.fullName.split(" ")[0] ?? me.user.fullName;

  const agendaQuery = {
    data:
      overdue.data && today.data && events.data
        ? { tasks: [...overdue.data, ...today.data], events: events.data }
        : undefined,
    isError: overdue.isError || today.isError || events.isError,
    error: overdue.error ?? today.error ?? events.error,
    refetch: () => Promise.all([overdue.refetch(), today.refetch(), events.refetch()]),
  };

  if (cockpit.isError && !cockpit.data) {
    return (
      <div className="page">
        <ErrorState error={cockpit.error} onRetry={() => void cockpit.refetch()} />
      </div>
    );
  }

  const c = cockpit.data;
  return (
    <div className="page page-home">
      {c ? (
        <Hero firstName={firstName} now={minute} cockpit={c} company={dash.data?.company} />
      ) : (
        <div className="hero skel" aria-busy="true" />
      )}

      {manager && floor.data && <Floor floor={floor.data} now={minute} />}

      <div className="home-grid">
        <div className="span-8">
          {c ? (
            <UpNext cockpit={c} now={now} licensedStates={me.licensedStates} />
          ) : (
            <section className="card">
              <div className="card-head">
                <h3>Up next</h3>
              </div>
              <CardBody query={cockpit} rows={5}>
                {() => null}
              </CardBody>
            </section>
          )}
        </div>
        <div className="span-4 full-when-narrow">
          {agendaQuery.data ? (
            <Agenda tasks={agendaQuery.data.tasks} events={agendaQuery.data.events} now={minute} />
          ) : (
            <section className="card">
              <div className="card-head">
                <h3>Today</h3>
              </div>
              <CardBody query={agendaQuery} rows={5}>
                {() => null}
              </CardBody>
            </section>
          )}
        </div>
      </div>

      {c && (
        <>
          <ActivityRow cockpit={c} />

          <div className="home-grid">
            <div className="span-5">
              <Leaderboard cockpit={c} meId={me.user.id} now={minute} />
            </div>
            <div className="span-4">
              <FloorWins wins={c.wins} meId={me.user.id} now={minute} />
            </div>
            <div className="span-3">
              <WeekByDay cockpit={c} />
            </div>
          </div>

          <div className="home-grid">
            <div className="span-8">
              <Pipeline lanes={c.pipeline} />
            </div>
            <div className="span-4">
              {can("commission:read") ? (
                <CommissionMonth c={c.commission} />
              ) : (
                <Bests b={c.bests} streakBest={c.streak.best} />
              )}
            </div>
          </div>

          <div className="home-grid">
            <div className={can("commission:read") ? "span-7" : "span-12"}>
              <Moments m={c.moments} />
            </div>
            {can("commission:read") && (
              <div className="span-5">
                <Bests b={c.bests} streakBest={c.streak.best} />
              </div>
            )}
          </div>
        </>
      )}

      <div className="home-divider">
        <span>Everyone's week</span>
        <Link to="/dashboards">All dashboards</Link>
      </div>
      {dash.isError ? (
        <ErrorState error={dash.error} onRetry={() => void dash.refetch()} />
      ) : dash.data ? (
        <Competition data={dash.data} meId={me.user.id} elapsed={c?.elapsed ?? 1} />
      ) : (
        <div className="stat-row" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="stat skel" style={{ height: 96 }} />
          ))}
        </div>
      )}
    </div>
  );
}
